import { Avatar } from '@/components/avatar';
import { Voter } from '@/interfaces';
import { formatDate } from '@/utils';
import {
  BookUser,
  CalendarDays,
  CreditCard,
  HeartPulse,
  MapPin,
  User,
  UserCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VotervoterProps {
  voter: Voter;
}

export const VoterDetails = ({ voter }: VotervoterProps) => {
  const { t } = useTranslation();

  const getEligibility = (value: boolean) => {
    switch (value) {
      case true:
        return (
          <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            {t('TextYes')}
          </span>
        );
      case false:
        return (
          <span className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
            {t('TextNo')}
          </span>
        );
      default:
        return <span className="italic text-gray-500">N/A</span>;
    }
  };

  return (
    <div className="flex w-full flex-col space-y-6 bg-white p-6 sm:p-7">
      <div className="space-y-5">
        <h4 className="flex items-center gap-3 text-lg font-semibold text-[#172641]">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#eaf3ff] text-[#2374cf]">
            <UserCircle className="h-5 w-5" strokeWidth={2} />
          </span>
          {t('TextVoterPersonalInfo')}
        </h4>
        <div className="flex flex-col gap-5 rounded-md border border-[#dfe8f4] bg-gradient-to-br from-[#f3f8ff] to-white p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-6">
          <div className="relative shrink-0">
            {voter.photoUrl || voter.photo ? (
              <div className="overflow-hidden rounded-md border-4 border-white shadow-[0_8px_24px_rgba(26,53,91,0.14)]">
                <img
                  className="h-[112px] w-[112px] object-cover"
                  src={voter.photoUrl || `data:image/jpeg;base64,${voter?.photo}`}
                  alt="Profile picture"
                />
              </div>
            ) : (
              voter.firstName &&
              voter.lastName && (
                <div className="overflow-hidden rounded-md border-4 border-white shadow-[0_8px_24px_rgba(26,53,91,0.14)]">
                  <Avatar
                    name={`${voter.firstName} ${voter.lastName}`}
                    size="xl"
                    className="!h-[112px] !w-[112px] !rounded-md bg-[#dcecff] text-xl text-[#195fae]"
                  />
                </div>
              )
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="mb-1 text-[14px] font-semibold tracking-[-0.03em] text-[#172641] sm:text-[16px]">
              {voter.firstName} {voter.lastName}
            </h3>
            {voter.nationalId && (
              <p className="mb-3 flex items-center gap-2 text-sm font-medium text-[#687791]">
                <CreditCard className="h-4 w-4 text-[#2374cf]" />
                {voter.nationalId}
              </p>
            )}
          </div>

          <div className="w-full shrink-0 border-t border-[#e1e8f2] pt-4 sm:ml-auto sm:w-[190px] sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#8290a5]">
              {t('TextSignature')}
            </p>
            {voter?.signatureUrl || voter?.signature ? (
              <img
                className="h-16 w-full rounded-md border border-[#dce5f1] bg-white object-contain p-2 shadow-sm"
                alt="signature"
                src={voter?.signatureUrl || `data:image/jpeg;base64,${voter?.signature}`}
              />
            ) : (
              <div className="flex h-16 items-center justify-center rounded-md border border-dashed border-[#ccd7e5] bg-white/70 px-3 text-center text-sm italic text-[#8290a5]">
                {t('TextNoSignature')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-[#e2e9f3] bg-white p-5 shadow-[0_8px_24px_rgba(26,53,91,0.04)] sm:p-6">
        <div className="space-y-5">
          <h5 className="flex items-center gap-2.5 border-b border-[#edf1f6] pb-3 font-semibold text-[#172641]">
            <User className="h-4 w-4 text-[#2374cf]" />
            {t('TextPersonalDetails')}
          </h5>

          <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            {voter.sex?.name && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <UserCircle className="w-full h-full" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8290a5]">{t('TextGender')}</p>
                  <p className="text-sm mt-0.5 font-medium text-[#263650]">{voter.sex.name}</p>
                </div>
              </div>
            )}

            {voter.dateOfBirth && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <CalendarDays className="w-full h-full" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8290a5]">{t('TextDateOfBirth')}</p>
                  <p className="text-sm mt-0.5 font-medium text-[#263650]">
                    {formatDate(voter.dateOfBirth, 'Africa/Johannesburg')}
                  </p>
                </div>
              </div>
            )}

            {voter.placeOfBirth && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <MapPin className="w-full h-full" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8290a5]">{t('TextPlaceOfBirth')}</p>
                  <p className="text-sm mt-0.5 font-medium text-[#263650]">{voter.placeOfBirth}</p>
                </div>
              </div>
            )}

            {voter.motherName && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <BookUser className="w-full h-full" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8290a5]">{t('TextMotherName')}</p>
                  <p className="text-sm mt-0.5 font-medium text-[#263650]">{voter.motherName}</p>
                </div>
              </div>
            )}

            {voter.fatherName && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <BookUser className="w-full h-full" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#8290a5]">{t('TextFatherName')}</p>
                  <p className="text-sm mt-0.5 font-medium text-[#263650]">{voter.fatherName}</p>
                </div>
              </div>
            )}

            {voter.status && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#f0f5fb] p-1.5 text-[#60708a]">
                  <HeartPulse className="w-full h-full" />
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium text-[#8290a5]">{t('TextEligibility')}</p>
                  <div>{getEligibility(voter.status.isEligible)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
